import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerStatus, BannerLayout } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { Checkbox } from 'ui-components/components/checkbox';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { RETURNS_INITIATION_OPTIONS } from './constants';
import { messages } from './messages';
import { ReturnsConfiguration } from './scenarios/returns-configuration';
import style from './style.module.less';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { useIsShipstationReturnsEnabled } from '../../../../../../hooks/use-seller-setting';
import { EventSourceStrings, ClickEvents } from '../../../../../../tracking/tracking-constants';
import { useReturnsContext } from '../../context/returns-context';

const HELP_CENTER_RETURNS_LINK = 'https://help.shipstation.com/hc/en-us/articles/49762271699483-Returns-in-ShipStation';

/**
 * Component for selecting how customers will initiate returns.
 * Displays checkboxes for self-service portal, return labels, and manual returns.
 * Shows expanded content when each option is checked.
 * @returns {JSX.Element} The rendered returns initiation options component
 */
export const ReturnsInitiationOptions = () => {
  const intl = useIntl();
  const isShipstationReturnsEnabled = useIsShipstationReturnsEnabled();
  const { selectedInitiationOptions, toggleInitiationOption } = useReturnsContext();

  const handleOptionChange = useCallback(
    (option) => {
      Segment.trackEvent(ClickEvents.FTU_RETURNS_INITIATION_OPTION_CLICKED, {
        source: EventSourceStrings.FTU_WIZARD_RETURNS_STEP,
        option,
        is_selected: !selectedInitiationOptions[option],
      });
      toggleInitiationOption(option);
    },
    [selectedInitiationOptions, toggleInitiationOption],
  );

  const isSelfServicePortalSelected = selectedInitiationOptions[RETURNS_INITIATION_OPTIONS.SELF_SERVICE_PORTAL];
  const isManualReturnsSelected = selectedInitiationOptions[RETURNS_INITIATION_OPTIONS.MANUAL_RETURNS];

  const handleSelfServicePortalChange = useCallback(() => {
    handleOptionChange(RETURNS_INITIATION_OPTIONS.SELF_SERVICE_PORTAL);
  }, [handleOptionChange]);

  const handleManualReturnsChange = useCallback(() => {
    handleOptionChange(RETURNS_INITIATION_OPTIONS.MANUAL_RETURNS);
  }, [handleOptionChange]);

  const manualReturnsBannerContent = (
    <span className={style['returns-initiation-banner-text']}>
      <FormattedMessage {...messages.manualReturnsDescription} />
      <Button
        className={style['returns-initiation-learn-more-link']}
        href={HELP_CENTER_RETURNS_LINK}
        rel='noopener noreferrer'
        target='_blank'
        theme={THEME_CONSTANTS.link}
      >
        <FormattedMessage {...messages.manualReturnsLearnMore} />
      </Button>
    </span>
  );

  return (
    <FlexColumn className={style['returns-initiation-options']}>
      <Text className={style['returns-step-title']} textType={TextType.H2} value={messages.howCustomersInitiateReturnsTitle} />
      <Text
        className={style['returns-step-description']}
        textType={TextType.Description}
        value={messages.howCustomersInitiateReturnsDescription}
      />

      <FlexColumn className={style['returns-initiation-checkboxes']} gap={Gap.Space12}>
        {/* Self-Service Portal Option */}
        <div className={style['returns-initiation-checkbox-item']} data-checked={isSelfServicePortalSelected}>
          <Checkbox
            checked={isSelfServicePortalSelected}
            label={intl.formatMessage(messages.selfServicePortalOption)}
            onChange={handleSelfServicePortalChange}
          />
          {isSelfServicePortalSelected ? (
            <FlexColumn className={style['returns-initiation-checkbox-content']}>
              <Text
                className={style['returns-initiation-description']}
                textType={TextType.Paragraph}
                value={isShipstationReturnsEnabled ? messages.selfServicePortalDescriptionNew : messages.selfServicePortalDescription}
              />
              <ReturnsConfiguration />
            </FlexColumn>
          ) : null}
        </div>

        {/* Manual Returns Option */}
        <div className={style['returns-initiation-checkbox-item']} data-checked={isManualReturnsSelected}>
          <Checkbox
            checked={isManualReturnsSelected}
            label={intl.formatMessage(messages.manualReturnsOption)}
            onChange={handleManualReturnsChange}
          />
          {isManualReturnsSelected ? (
            <FlexColumn className={style['returns-initiation-checkbox-content']}>
              <BannerMessageV2
                className={style['returns-initiation-banner']}
                content={manualReturnsBannerContent}
                layout={BannerLayout.Full}
                status={BannerStatus.Info}
              />
            </FlexColumn>
          ) : null}
        </div>
      </FlexColumn>
    </FlexColumn>
  );
};
